import React, { useState } from "react";
import { toast } from "sonner";
import { ShieldUser, Download, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const LBL = {
  en: {
    title: "Data subject access request",
    desc: "Enter the email address of a data subject to compile and download every piece of personal data the platform holds about them (all reports where they are the reporter, updates and dispute messages) as a GDPR / DSA data export PDF.",
    placeholder: "person@example.com",
    generate: "Generate PDF export",
    empty: "Enter an email address.",
    ok: "Data export downloaded",
    err: "Could not generate the export",
    hint: "The export is generated in English and downloaded directly to your device.",
  },
  hu: {
    title: "Adatigénylés (érintetti hozzáférés)",
    desc: "Add meg az érintett e-mail címét, hogy egyetlen GDPR / DSA adatexport PDF-be gyűjtsük és letöltsük az összes róla tárolt személyes adatot (minden bejelentés, ahol ő a bejelentő, a frissítések és a vitaüzenetek).",
    placeholder: "szemely@pelda.hu",
    generate: "PDF export készítése",
    empty: "Adj meg egy e-mail címet.",
    ok: "Adatexport letöltve",
    err: "Az export készítése sikertelen",
    hint: "Az export angol nyelven készül és közvetlenül az eszközödre töltődik le.",
  },
  de: {
    title: "Auskunftsersuchen der betroffenen Person",
    desc: "Geben Sie die E-Mail-Adresse einer betroffenen Person ein, um alle über sie gespeicherten personenbezogenen Daten (alle Meldungen als Melder, Aktualisierungen und Streitnachrichten) als GDPR / DSA-Datenexport-PDF zusammenzustellen und herunterzuladen.",
    placeholder: "person@beispiel.de",
    generate: "PDF-Export erstellen",
    empty: "Bitte E-Mail-Adresse eingeben.",
    ok: "Datenexport heruntergeladen",
    err: "Export konnte nicht erstellt werden",
    hint: "Der Export wird auf Englisch erstellt und direkt heruntergeladen.",
  },
  fr: {
    title: "Demande d'accès de la personne concernée",
    desc: "Saisissez l'adresse e-mail d'une personne concernée pour compiler et télécharger toutes les données personnelles détenues à son sujet (tous les signalements où elle est signalant, mises à jour et messages de litige) sous forme de PDF d'export RGPD / DSA.",
    placeholder: "personne@exemple.fr",
    generate: "Générer l'export PDF",
    empty: "Saisissez une adresse e-mail.",
    ok: "Export de données téléchargé",
    err: "Impossible de générer l'export",
    hint: "L'export est généré en anglais et téléchargé directement sur votre appareil.",
  },
};

export function ModDataRequest() {
  const { lang } = useLang();
  const t = LBL[lang] || LBL.en;
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    const e = email.trim();
    if (!e) {
      toast.error(t.empty);
      return;
    }
    setBusy(true);
    try {
      const res = await api.get(`/mod/data-request`, { params: { email: e }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `data-request-${e.replace(/[^a-zA-Z0-9._-]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t.ok);
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="mod-data-request">
      <div className="rounded-2xl border border-zinc-800/70 bg-[#111113] p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl border border-cyan-500/30 bg-cyan-500/10 text-cyan-300">
            <ShieldUser size={22} />
          </div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-zinc-50">{t.title}</h2>
            <p className="text-[12px] text-zinc-500">GDPR · DSA</p>
          </div>
        </div>

        <p className="mt-5 text-[13px] leading-relaxed text-zinc-400">{t.desc}</p>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input
              data-testid="data-request-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && generate()}
              placeholder={t.placeholder}
              className="h-11 border-zinc-800 bg-zinc-950 pl-9 text-[13px] text-zinc-100"
            />
          </div>
          <Button
            data-testid="data-request-generate"
            onClick={generate}
            disabled={busy}
            className="h-11 gap-2 bg-cyan-600 px-5 text-[13px] font-semibold text-white hover:bg-cyan-500"
          >
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {t.generate}
          </Button>
        </div>

        <p className="mt-3 text-[12px] text-zinc-600">{t.hint}</p>
      </div>
    </div>
  );
}
